import { useEffect, useRef, useState } from 'react';
import { toOutcome, useSession, type HuacaClient, type ModuleScreenContext } from '@huaca/ui';
import { PlanLedger } from './PlanLedger';
import { config } from './config';
import { SearchSkeleton } from './Skeleton';

/**
 * The one thing this product does differently from every other module.
 *
 * Calculator and claro send a first-time user to a pricing page. Here the natural flow provisions a
 * free 3-day trial and drops the user straight into the search screen, so nobody is asked to choose
 * a plan before they have seen a single tender.
 *
 * IT STILL HAS TO BRANCH. ModuleApp reaches this screen from TWO places: the `/plans` route (which
 * has no guard at all) and the default branch when an owner has no entitlement. So it is also where
 * a user lands on day 4 when the trial has lapsed, and where an existing customer lands to upgrade.
 * Enrolling unconditionally would mean a lapsed user silently attempting a second trial instead of
 * being shown the paid plans.
 */

type Result = 'entered' | 'show-plans';

/**
 * The enrolment, memoised as a PROMISE rather than guarded by a boolean — and the difference is
 * visible to the user.
 *
 * `ctx` is not referentially stable: AuthProvider rebuilds its context value whenever session state
 * changes, so the `reload()` inside this very sequence produces a new `ctx`, a new callback, and a
 * re-run of the mount effect. A boolean guard has nothing useful to say on that second run — the
 * previous version answered "already attempted, so show the plans", which flashed the pricing page
 * for exactly as long as it took to navigate away. Every mount now awaits the SAME promise, so a
 * re-run cannot reach a different conclusion from the run that is still in flight, and the screen
 * only ever changes when the work actually decides something.
 */
let inFlight: Promise<Result> | null = null;

async function enrolOnce(client: HuacaClient, ctx: ModuleScreenContext): Promise<Result> {
  inFlight ??= (async () => {
    /**
     * `orgSubscriptions()` rather than the session's own list, because it reports subscriptions in
     * EVERY state — including a lapsed trial, which `/auth/me` has already stopped counting. That
     * difference is the whole branch: without it, day 4 looks identical to day 0.
     */
    const { subscriptions } = await client.orgSubscriptions();
    if (subscriptions.some((s) => s.productId === config.productId)) return 'show-plans';

    await client.startTrial(`${config.productId}.trial`);

    /**
     * ORDER MATTERS. `reload()` first so `me` reports the new seat, and only then navigate — because
     * ModuleApp's `case '/plans'` has no entitlement check, so while the path is still `/plans` it
     * re-enters this screen no matter what the session says. Navigating first would land on `/` with
     * a stale `me`, which routes an unentitled owner straight back here.
     */
    await ctx.reload();
    ctx.navigate('/');
    return 'entered';
  })();

  return inFlight;
}

type Phase = 'working' | 'plans' | 'error';

export function AutoEnroll({ ctx }: { ctx: ModuleScreenContext }) {
  const { client } = useSession();
  const [phase, setPhase] = useState<Phase>('working');
  const [message, setMessage] = useState('');

  /** Held in a ref so a new `ctx` identity cannot re-trigger the effect — only `client` may. */
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    let alive = true;

    enrolOnce(client, ctxRef.current).then(
      (result) => {
        // 'entered' deliberately leaves the spinner up: the navigation away is already under way,
        // and swapping in another screen for those few frames is the flash this exists to prevent.
        if (alive && result === 'show-plans') setPhase('plans');
      },
      (err) => {
        if (!alive) return;
        /**
         * The rejected promise is deliberately KEPT. A later mount replays the same rejection and
         * shows the same error, rather than quietly firing another enrolment attempt at a server
         * that just failed — retrying is the user's decision, made with the button below.
         */
        const outcome = toOutcome(err);
        if (outcome.kind === 'trial-used') {
          // This org already used its free trial. Not an error — it is the paid-plans case.
          setPhase('plans');
          return;
        }
        setMessage(
          outcome.kind === 'unauthenticated' ? 'החיבור פג. יש להתחבר מחדש.' : 'לא הצלחנו להתחיל את ההתנסות.',
        );
        setPhase('error');
      },
    );

    return () => {
      alive = false;
    };
  }, [client]);

  if (phase === 'plans') return <PlanLedger productId={config.productId} />;

  if (phase === 'error') {
    // Deliberately NOT a silent spinner. A failed enrolment leaves the user with no entitlement and
    // no explanation, and an endless spinner is indistinguishable from a slow network.
    return (
      <section data-testid="enrol-error">
        <h1>לא הצלחנו להתחיל את ההתנסות</h1>
        <p role="alert">{message}</p>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            // Clearing the memo is what makes this a real retry rather than a replay of the
            // stored rejection.
            inFlight = null;
            setPhase('working');
            enrolOnce(client, ctxRef.current).then(
              (result) => result === 'show-plans' && setPhase('plans'),
              () => setPhase('error'),
            );
          }}
        >
          ניסיון נוסף
        </button>
      </section>
    );
  }

  /**
   * The SEARCH SCREEN'S skeleton, not a message about provisioning.
   *
   * Announcing "opening a 3-day trial for you" narrates our own bookkeeping — something the user
   * never asked for, cannot act on, and which only invites them to wonder whether it might fail.
   * Worse, it is a differently-shaped screen, so the wait used to be three visible states: message,
   * then an empty page with placeholder text, then content. Rendering the destination's skeleton
   * here means the layout appears once and simply fills in.
   */
  return (
    <div data-testid="enrol-progress">
      <SearchSkeleton />
    </div>
  );
}

/** Test-only: the module-level memo has to be resettable between cases. */
export function __resetEnrolGuard(): void {
  inFlight = null;
}
